import React, { useState } from 'react';
import {
  Bug,
  ChevronDown,
  ChevronUp,
  Shield,
  User,
  Eye,
  Info,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useDebugRole, DebugRole } from '@/contexts/DebugRoleContext';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useSubmissionFormContext } from '@/features/submission_forms/SubmissionFormContext';
import { isClarificationsActiveStatus } from '@/utils/clarificationStatus';
import { isDevelopmentEnv } from '@/utils/environment';

const ROLE_OPTIONS: {
  value: DebugRole;
  label: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: 'real',
    label: 'Real',
    icon: <Eye className="h-3.5 w-3.5" />,
    description: 'Use actual backend role & permissions',
  },
  {
    value: 'admin',
    label: 'IRB Ops',
    icon: <Shield className="h-3.5 w-3.5" />,
    description:
      'Internal reviewer — can add/resolve clarifications and request responses',
  },
  {
    value: 'customer',
    label: 'Customer',
    icon: <User className="h-3.5 w-3.5" />,
    description: 'Can only reply when clarifications are requested',
  },
];

const getRoundStateLabel = (roundState: string | null | undefined): string => {
  switch (roundState) {
    case 'drafting':
      return 'Drafting (not sent)';
    case 'requested':
      return 'Requested';
    case 'responded':
      return 'Responded';
    case 'resolved':
      return 'Resolved';
    default:
      return 'None';
  }
};

const getRoundStateBadgeClass = (
  roundState: string | null | undefined
): string => {
  switch (roundState) {
    case 'drafting':
      return 'bg-gray-100 text-gray-600';
    case 'requested':
      return 'bg-amber-100 text-amber-800';
    case 'responded':
      return 'bg-green-100 text-green-800';
    case 'resolved':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const DebugRolePanel: React.FC = () => {
  const {
    debugRole,
    setDebugRole,
    isOverrideActive,
    clarificationsEnabled,
    setClarificationsEnabled,
  } = useDebugRole();
  const [isExpanded, setIsExpanded] = useState(false);

  // Get current workflow state from contexts
  const {
    clarificationRoundState,
    canAddClarifications,
    canResolveClarifications,
    canReplyOnly,
    clarificationsByQuestion,
  } = useClarificationsContext();

  const { submissionFormData } = useSubmissionFormContext();

  if (!isDevelopmentEnv()) return null;

  const submissionStatus = submissionFormData?.status;
  const totalClarifications = Object.values(clarificationsByQuestion).reduce(
    (sum, arr) => sum + arr.length,
    0
  );
  const isPending = clarificationRoundState === 'requested';

  // Contextual tips based on current state and selected role
  const getTip = (): string | null => {
    if (debugRole === 'customer' && !isPending) {
      return 'Customer view is empty because the clarification round is not "requested". Switch to IRB Ops and click "Request Clarifications" in the sidebar first.';
    }
    if (debugRole === 'customer' && isPending && totalClarifications === 0) {
      return 'No clarifications have been added yet. Switch to IRB Ops and add some clarifications first.';
    }
    if (
      debugRole === 'admin' &&
      !isClarificationsActiveStatus(submissionStatus)
    ) {
      if (submissionStatus === 'draft') {
        return 'Submission is still a draft. Clarifications are only available after submission.';
      }
      return 'Clarifications are not available in this submission status.';
    }
    return null;
  };

  const tip = getTip();

  return (
    <div className="fixed bottom-28 right-6 z-[9999] print:hidden">
      {/* Collapsed pill */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-xs font-medium transition-all ${
          isOverrideActive || !clarificationsEnabled
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
        }`}
      >
        <Bug className="h-3.5 w-3.5" />
        <span>
          Role: {ROLE_OPTIONS.find(o => o.value === debugRole)?.label}
          {!clarificationsEnabled && ' · Clarifications OFF'}
        </span>
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronUp className="h-3 w-3" />
        )}
      </button>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="absolute bottom-12 right-0 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Bug className="h-4 w-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-900">
                Debug: Role Switcher
              </h3>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Override role to test clarification workflow views
            </p>
          </div>

          {/* Current workflow state */}
          <div className="px-4 py-3 border-b border-gray-100 space-y-1.5">
            <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
              Workflow State
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                {submissionStatus || 'unknown'}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoundStateBadgeClass(clarificationRoundState)}`}
              >
                {getRoundStateLabel(clarificationRoundState)}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>
                {totalClarifications} clarification
                {totalClarifications !== 1 ? 's' : ''}
              </span>
              <span>·</span>
              <span>{canAddClarifications ? 'Can add' : 'Cannot add'}</span>
              <span>·</span>
              <span>
                {canReplyOnly
                  ? 'Reply only'
                  : canResolveClarifications
                    ? 'Can resolve'
                    : 'No resolve'}
              </span>
            </div>
          </div>

          {/* Feature Flag: Clarifications */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              Feature Flags
            </p>
            <button
              type="button"
              onClick={() => setClarificationsEnabled(!clarificationsEnabled)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {clarificationsEnabled ? (
                  <ToggleRight className="h-5 w-5 text-green-600" />
                ) : (
                  <ToggleLeft className="h-5 w-5 text-gray-400" />
                )}
                <span
                  className={`text-sm font-medium ${clarificationsEnabled ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  Clarifications
                </span>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  clarificationsEnabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {clarificationsEnabled ? 'ON' : 'OFF'}
              </span>
            </button>
          </div>

          {/* Role selector */}
          <div className="p-2">
            {ROLE_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setDebugRole(option.value);
                }}
                className={`w-full text-left px-3 py-2.5 rounded-md transition-colors flex items-start gap-3 ${
                  debugRole === option.value
                    ? 'bg-purple-50 border border-purple-200'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <div
                  className={`mt-0.5 flex-shrink-0 ${
                    debugRole === option.value
                      ? 'text-purple-600'
                      : 'text-gray-400'
                  }`}
                >
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-sm font-medium ${
                      debugRole === option.value
                        ? 'text-purple-900'
                        : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                    {debugRole === option.value && (
                      <span className="ml-2 text-xs text-purple-500">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {option.description}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Contextual tip */}
          {tip && (
            <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-100">
              <div className="flex gap-2">
                <Info className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">{tip}</p>
              </div>
            </div>
          )}

          {/* Override active indicator */}
          {isOverrideActive && !tip && (
            <div className="px-4 py-2 bg-purple-50 border-t border-purple-100">
              <p className="text-xs text-purple-700">
                Override active — backend permissions are being simulated
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DebugRolePanel;
